import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { GraphQLFormattedError } from "graphql";
import * as Sentry from "@sentry/react";
import {
  isAbortedNetworkError,
  isNotAuthorizedGraphQLError,
} from "shared/src/utils/apollo-error-utils";
import { signOutUser } from "./sign-out";

let signOutInFlight = false;

async function forceSignOutDueToUnauthorized() {
  if (signOutInFlight) return;
  signOutInFlight = true;
  await signOutUser();
}

export const apolloErrorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    const unauthorizedError = error.errors.find(isNotAuthorizedGraphQLError);
    if (unauthorizedError) {
      Sentry.captureMessage(
        `Signing user out due to GraphQL "Not authorized" response on ${operation.operationName || "Unknown Operation"}`,
        {
          level: "warning",
          tags: {
            errorType: "graphql-not-authorized",
            operation: operation.operationName || "unknown",
            source: "apollo-error-link",
          },
          extra: {
            path: unauthorizedError.path,
            operation: operation.operationName,
            variables: operation.variables,
          },
          fingerprint: [
            "graphql-not-authorized",
            operation.operationName || "unknown",
          ],
        },
      );
      void forceSignOutDueToUnauthorized();
      return;
    }

    error.errors.forEach((graphqlError: GraphQLFormattedError) => {
      const errorInfo = {
        type: "GraphQL Error",
        message: graphqlError.message,
        path: graphqlError.path,
        extensions: graphqlError.extensions,
        operation: operation.operationName,
        variables: operation.variables,
      };

      const sentryError = new Error(
        `GraphQL Error in ${operation.operationName || "Unknown Operation"}: ${graphqlError.message}`,
      );
      sentryError.name = "GraphQLError";

      Sentry.captureException(sentryError, {
        tags: {
          errorType: "graphql",
          operation: operation.operationName || "unknown",
          source: "apollo-error-link",
        },
        extra: errorInfo,
        fingerprint: [
          "graphql-error",
          operation.operationName || "unknown",
          graphqlError.message,
        ],
      });
    });
    return;
  }

  const networkError = error as Error;
  const errorType =
    "statusCode" in networkError
      ? `Network Error (${(networkError as { statusCode: number }).statusCode})`
      : "Network Error";

  const errorInfo = {
    type: errorType,
    message: networkError.message,
    operation: operation.operationName,
    variables: operation.variables,
  };

  if (isAbortedNetworkError(networkError)) {
    // oxlint-disable-next-line no-console
    console.warn(
      `[Skipped Sentry Report]: ${operation.operationName || "Unknown Operation"} - ${networkError.message}`,
    );
    return;
  }

  const sentryError = new Error(
    `${errorType} in ${operation.operationName || "Unknown Operation"}: ${networkError.message}`,
  );
  sentryError.name = errorType.replace(/[()]/g, "").replace(/\s+/g, "");

  Sentry.captureException(sentryError, {
    tags: {
      errorType: "network",
      operation: operation.operationName || "unknown",
      source: "apollo-error-link",
      ...("statusCode" in networkError && {
        statusCode: String((networkError as { statusCode: number }).statusCode),
      }),
    },
    extra: errorInfo,
    fingerprint: [
      "network-error",
      operation.operationName || "unknown",
      errorType,
    ],
  });
});

export function createEnhancedApolloErrorLink() {
  return apolloErrorLink;
}
